import { TRAVELINFO_TRIP_BASEURL, TRAVELINFO_TRIP_KEY } from '../constants'
import { getPassageTime } from '../utils/date.utils'
import { enrichTripsLineDirections } from './enrichment.service'
import {
  DisplayPassage,
  isTripValid,
  mapTripsToPassagesWithLine,
} from './mappers'

type ScheduleType = 'PLANNED' | 'NOT_SERVED' | 'REPLACEMENT' | 'PASSAGE_CHANGE'
type RealtimeStatus =
  | 'DELETED'
  | 'EXPIRED'
  | 'REALTIME'
  | 'NOREALTIME'
  | 'WAITINGFORCONNECTION'
  | 'JAMMED'
  | 'NOALIGHTING'
  | 'NOBOARDING'
  | 'NOSTOP'
  | 'ATSTOP'
  | 'DEPARTING'
  | 'DEPARTED'
  | 'PASSING'
  | 'CANCELLED'

export type StopTripPassage = {
  merged: boolean
  plannedPassage: {
    arrivalDateTime: string // '2024-05-07T05:09:00+0200',
    arrivalEpoch: number // 1715051340000,
    departureDateTime: string // '2024-05-07T05:09:00+0200',
    departureEpoch: number // 1715051340000
  }
  scheduleType: ScheduleType
  passageStatus?: 'CANCELLED'
  realtimeStatuses?: RealtimeStatus[]
  visitNr: number // 16
  lastStop?: boolean
  realtimePassage?: {
    arrivalDateTime: string // "2024-05-07T09:29:00+0200"
    arrivalEpoch: number // 1715066940000
    departureDateTime: string // "2024-05-07T09:29:00+0200"
    departureEpoch: number // 1715066940000
  }
}
export type FromBackendStopTrip = {
  exploitationDate: string // "2024-05-07"
  id: string // "2024-05-07_1009_4"
  lineDirection: {
    directionCode: string
    id: string
    line: {
      id: string
    }
  }
  nr: string // "4"
  passages: StopTripPassage[]
  patternId: string // "1009125"
  placeDestination: string // "Antwerpen P+R Linkeroever"
  planningDestination: string // "P+R Linkeroever"
  planningViaDestination?: string // "Via E19"
  realtimeViaDestination?: string // "Via E19"
}
type FromBackendStopsTrips = {
  serverDateTime: number
  trips: FromBackendStopTrip[]
}

async function getMultivertrekkenForStop({
  haltenummer,
  type = 'MERGEFLEX',
  exploitationDate,
}: {
  haltenummer: string
  type: string
  exploitationDate?: string
}) {
  const searchParams = new URLSearchParams({
    type,
    enrichmentType: 'NONE',
  })

  if (exploitationDate) {
    searchParams.set('exploitationDate', exploitationDate)
  }

  const url =
    `${TRAVELINFO_TRIP_BASEURL}/stops/${haltenummer}/trips?` +
    searchParams.toString()

  const { trips, serverDateTime }: FromBackendStopsTrips = await fetch(url, {
    headers: {
      'Ocp-Apim-Subscription-Key': TRAVELINFO_TRIP_KEY as string,
    },
  }).then((data) => data.json())

  const enrichedTrips = await enrichTripsLineDirections(trips)
  return {
    passages: mapTripsToPassagesWithLine(enrichedTrips),
    serverTime: serverDateTime,
  }
}

export async function getMultivertrekken({
  haltenummers,
  includeEindHaltes,
}: {
  haltenummers: string
  includeEindHaltes: boolean
}) {
  const haltenummerArray = haltenummers.split('+')
  const results: PromiseSettledResult<{
    passages: DisplayPassage[]
    serverTime: number
  }>[] = await Promise.allSettled(
    haltenummerArray.map((haltenummer: string) =>
      getMultivertrekkenForStop({
        haltenummer,
        type: 'MERGEFLEX',
      }),
    ),
  )

  let hasErrors = false
  let serverTime = Date.now()
  const passages: DisplayPassage[] = results
    .reduce((allPassages: DisplayPassage[], nextResult) => {
      if (nextResult.status === 'fulfilled') {
        serverTime = nextResult.value.serverTime || serverTime
        return allPassages.concat(nextResult.value.passages)
      }

      hasErrors = true
      return allPassages
    }, [])
    .filter((passage) => isTripValid(passage, includeEindHaltes, serverTime))
    .sort((a, b) => {
      return getPassageTime(a) - getPassageTime(b)
    })

  if (!passages.length && hasErrors) {
    throw new Error(
      'No passages were returned and at least one call had an error',
    )
  }

  return {
    passages,
    serverTime,
  }
}

export async function getTheoreticalVertrekken({
  haltenummers,
  exploitationDate,
}: {
  haltenummers: string[]
  exploitationDate: string
}) {
  const results: PromiseSettledResult<{
    passages: DisplayPassage[]
    serverTime: number
  }>[] = await Promise.allSettled(
    haltenummers.map((haltenummer) =>
      getMultivertrekkenForStop({
        haltenummer,
        type: 'PLANNING',
        exploitationDate,
      }),
    ),
  )

  let hasErrors = false
  const passages = results
    .reduce((allPassages: DisplayPassage[], nextResult) => {
      if (nextResult.status === 'fulfilled') {
        return allPassages.concat(nextResult.value.passages)
      }

      hasErrors = true
      return allPassages
    }, [])
    .sort((a, b) => {
      return getPassageTime(a) - getPassageTime(b)
    })

  if (!passages.length && hasErrors) {
    throw new Error(
      'No passages were returned and at least one call had an error',
    )
  }
  return passages
}
